import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { CONFIG_MODE, IaConfigStatusBody, IaConfigStatusHeader } from 'src/app/core/enums/insightConfigurations.enum';
import { MODAL_TITLE, PREDICTIVE_CONFIG_ERROR, PREDICTIVE_CONFIG_ERROR_MODAL_MESSAGE, PREDICTIVE_CREATE_COMPONENTS } from 'src/app/core/enums/predictiveFailure.enum';
import { AlgorithmList, CreateConfigError, PredictiveFailureConfiguration, SelectedKpi } from 'src/app/core/models/insight-configuration.model';
import { Step } from 'src/app/core/models/Step';
import { ClearPredictiveFailureState, GetAlgorithmAssetList, GetThemes, MoveStep, RemoveConfiguredAssets, SetAssetsDuplicateDetected, SetPredictiveFailureMode, SetThresholdUomConfig, SubmitPredictiveFailureConfiguration, UpdateConfiguraitonDetails } from 'src/app/core/store/predictive-failure-configuration/predictive-failure-configuration.action';
import { IssueModalComponent } from 'src/app/shared/components/issue-modal/issue-modal.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.enum';
import { SnackbarService } from 'src/app/shared/components/snackbar/snackbar.service';

export enum PREDICTIVE_FAILURE_MODE {
  STEPS,
  SUBMIT,
  CANCEL,
  BACK
}
@Component({
  selector: 'app-predictive-failure-config',
  templateUrl: './predictive-failure-config.component.html',
  styleUrls: ['./predictive-failure-config.component.scss']
})
export class PredictiveFailureConfigComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stepper1', { static: false }) public stepper1: MatStepper;
  @ViewChild('stepper2', { static: false }) public stepper2: MatStepper;
  
  public selectedIndex = 0;
  public currentStep = 0;
  public showConfigurationDropdown: boolean = false; 
  public showAlgorithmDropdown: boolean = true;
  public isValid: boolean = false;
  public predictiveFailureMode = PREDICTIVE_FAILURE_MODE;
  public rightButtonMode;
  public leftButtonMode;
  public currentSelectedAlgorithm: any;
  public isDisabled: boolean = false;
  public selectedConfigType: any;
  public selectedAsset : any[];
  public configurationDetails : any;
  public selectedImpactedKPIs : SelectedKpi[];
  public unsubscribe  = new Subject<void>();
  public predictiveFailureConfiguration : PredictiveFailureConfiguration;

  public steps: Step[];
  
  public stateSeletedIndex = 0;

  public loading = false;

  public algorithmList: AlgorithmList[];
  public selectedAlgorithm: any;

  public isLoading : boolean = true;

  public issueModalRef: BsModalRef;
  public issueModalClosed$ = new Subject<void>();

  public assetsDuplicated: boolean = false; 

  public mode = CONFIG_MODE.CREATE;
  public configurationUpdated = false;
  public disableOnSubmit: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    public modalService: BsModalService,
    public snackbarService: SnackbarService
  ) { }

  ngOnInit(): void {
    this.rightButtonMode = this.predictiveFailureMode.STEPS;
    this.leftButtonMode = this.predictiveFailureMode.CANCEL;

    this.mode = this.store.selectSnapshot(state => state.configurations.predictiveFailure.mode);
    
    if(this.mode === CONFIG_MODE.EDIT) {
      this.store.dispatch(new MoveStep(PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS));
    } else {
      this.isLoading = true;
      this.store.dispatch(new ClearPredictiveFailureState());
      this.store.dispatch(new MoveStep(PREDICTIVE_CREATE_COMPONENTS.ALGORITHM));
      this.getConfigType();
    }

    this.getThemes();
    this.store.select(state => state.configurations.predictiveFailure.isAlgoLoading).pipe(takeUntil(this.unsubscribe))
    .subscribe(isAlgoLoading => {
      this.isLoading = isAlgoLoading
    })
  }

  ngAfterViewInit(): void {
    this.getStepStates();
    this.getActiveStepIndex();
    this.listenToAssetDuplicates();
    if(this.mode === CONFIG_MODE.EDIT) {
      this.getEditState();
    }
    this.changeDetectorRef.detectChanges();
    
  }

  ngAfterContentChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public navigateToLanding(): void {
    this.store.dispatch(new SetPredictiveFailureMode(null))
    this.router.navigateByUrl('/home')
  }
  
  public getConfigType() {
    this.store.select(state => state.configurations.selectedConfigType).pipe(takeUntil(this.unsubscribe)).subscribe(ConfigType => {
      this.selectedConfigType = ConfigType;
      if(this.selectedConfigType === null) {
        this.navigateToLanding();
      } else {
        this.store.dispatch(new SetThresholdUomConfig(this.selectedConfigType.thresholdUomConfig))
      }
    })
  }

  public submitConfiguration() {
    this.loading = false;
    this.disableOnSubmit = true;
  
    // Trigger submission of configuration via state management
    this.store.dispatch(new SubmitPredictiveFailureConfiguration())
    .pipe(
      catchError(error => {
        // Handle error from thrown from state
        this.handleCreateConfigError(error)
        return of();
      }),
      takeUntil(this.unsubscribe)
    )
    .subscribe(() => {
      this.disableOnSubmit = true;
      this.snackbarService.show(SnackbarType.SUCCESS, IaConfigStatusHeader.SUCCESS, IaConfigStatusBody.SUCCESS);
      this.store.dispatch(new SetPredictiveFailureMode(null));
      this.backToLanding();
    })
  }

  public updateConfiguration() {
    this.loading = false;
    this.disableOnSubmit = true;

    this.store.dispatch(new SetPredictiveFailureMode(null));
    this.store.dispatch(new UpdateConfiguraitonDetails())
    .pipe(
      takeUntil(this.unsubscribe),
      catchError(() => {
        this.snackbarService.show(SnackbarType.FAILED, IaConfigStatusHeader.FAILED, IaConfigStatusBody.UPDATE_FAILED);
        this.backToLanding();
        return of()
      })
    )
    .subscribe(() => {
      this.disableOnSubmit = true;
      this.snackbarService.show(SnackbarType.SUCCESS, IaConfigStatusHeader.SUCCESS, IaConfigStatusBody.UPDATE_SUCCESS);
      this.backToLanding();
    })
  }

  public backToLanding() {
    // Clear the current state of configuration and return to landing page
    this.store.dispatch(new ClearPredictiveFailureState());
    this.loading = false;
    this.navigateToLanding();
  }

  public handleCreateConfigError(configError: CreateConfigError) {
    if(configError === null) {
      this.snackbarService.show(SnackbarType.FAILED, IaConfigStatusHeader.FAILED, IaConfigStatusBody.FAILED);
      this.store.dispatch(new SetPredictiveFailureMode(null));
      this.backToLanding();
    } else if(configError.errorCode == PREDICTIVE_CONFIG_ERROR.CONFIG_EXISTS) {
       // Reload the asset list in the background
       const selectedAlgorithm = this.store.selectSnapshot(state => state.configurations.predictiveFailure.selectedAlgorithm);
       this.store.dispatch(new GetAlgorithmAssetList(selectedAlgorithm, true));
       // trigger assets are duplicated
       this.store.dispatch(new SetAssetsDuplicateDetected(true));

       //Get the duplicates
       let duplicateAssets = configError.details.configuredAssets;
       let existingConfigurations = duplicateAssets.map(asset => asset.assetName)
       // Show the issue modal
       this.openIssueModal(existingConfigurations);
   
       // Listen when modal is closed
       const sub = new Subject<void>();
       this.issueModalClosed$.pipe(takeUntil(sub)).subscribe(() => {
         // Redirect to step 2
         this.disableOnSubmit = false;
         this.loading = false;
         this.store.dispatch(new MoveStep(PREDICTIVE_CREATE_COMPONENTS.ASSETS))
         sub.next();
         sub.complete();
       });
    }
  }

  public getStepStates() {
    this.store.select(state => state.configurations.predictiveFailure.steps).pipe(takeUntil(this.unsubscribe)).subscribe(steps => {
      this.steps = steps;
      this.checkActiveComponentValidity();
    })
  }

  public getEditState() {
    this.store.select(state => state.configurations.predictiveFailure.configurationUpdated).pipe(takeUntil(this.unsubscribe))
    .subscribe(updated => {
      this.configurationUpdated = updated;
      this.checkActiveComponentValidity();
    })
  }

  public listenToAssetDuplicates() {
    this.store.select(state => state.configurations.predictiveFailure.assetsDuplicateDetected).pipe(takeUntil(this.unsubscribe)).subscribe(duplicate => {
      this.assetsDuplicated = duplicate;
    })
  }

  public checkActiveComponentValidity() {
    if(this.mode === CONFIG_MODE.EDIT && this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.IMPACTED_KPIS) {
      this.isDisabled = !this.configurationUpdated;
    } else{
      let valid = !!this.steps.find(step => step.stepIndex === this.selectedIndex)?.valid;
      this.isDisabled = !valid;
    }
  }

  public getActiveStepIndex() {
    this.store.select(state => state.configurations.predictiveFailure.activeStep).pipe(takeUntil(this.unsubscribe)).subscribe(activeStep => {
      this.selectedIndex = activeStep;
      this.evaluateSteps();
      this.checkActiveComponentValidity();
    })
  }

  public evaluateSteps() {
    if(this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.ALGORITHM || this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.ASSETS) {
      this.showConfigurationDropdown = false;
      this.showAlgorithmDropdown = true;

      this.stepper1.selectedIndex = this.selectedIndex;
      this.stepper1.selected.completed = this.selectedIndex == 1;
      
    } else if(this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS || this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.IMPACTED_KPIS){
      this.showAlgorithmDropdown = false;
      this.showConfigurationDropdown = true;
      this.stepper2.selectedIndex = (this.selectedIndex - 2);
    }
  }

  public onLeftButtonClick() {
    if(this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.ALGORITHM) {
      this.store.dispatch(new ClearPredictiveFailureState())
      this.navigateToLanding();
    } else {
      this.store.dispatch(new MoveStep(--this.selectedIndex));
    }
  }

  public onRightButtonClick() {
    if(this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.IMPACTED_KPIS && this.mode === CONFIG_MODE.CREATE) {
      this.submitConfiguration();
    } else if(this.selectedIndex === PREDICTIVE_CREATE_COMPONENTS.IMPACTED_KPIS && this.mode === CONFIG_MODE.EDIT) {
      this.updateConfiguration();
    } else if(this.selectedIndex < PREDICTIVE_CREATE_COMPONENTS.IMPACTED_KPIS) {
       this.store.dispatch(new MoveStep(++this.selectedIndex));
    }
  }

  public onRightErrorButtonClick() {
    if(this.selectedIndex == PREDICTIVE_CREATE_COMPONENTS.ASSETS) {
      // Remove configured assets when clicked
      this.store.dispatch(new RemoveConfiguredAssets())
    }
  }

  public getThemes() {
    let configId = '';
    if(this.mode === CONFIG_MODE.EDIT){
      configId = this.store.selectSnapshot(state => state.configurations.predictiveFailure.configurationDetailsSaved?.categoryId);
    }else{
      configId = this.store.selectSnapshot(state => state.configurations.selectedConfigType?.viewId);
    }
    this.store.dispatch(new GetThemes(configId))
  }

  public algoLoading(algoLoading : any) {
    this.isLoading = algoLoading;
  }

  public openIssueModal(existingConfigurations: string[] = []) {
    this.modalService.show(IssueModalComponent, {
      initialState: {
        title: MODAL_TITLE.SUBMIT_ISSUE,
        message: PREDICTIVE_CONFIG_ERROR_MODAL_MESSAGE.SUBMIT_ISSUE,
        existingConfigurations: existingConfigurations,
        issueModalClosed$: this.issueModalClosed$
      },
      class: 'message-component issue-modal predictive',
      backdrop: 'static'
    })
  }

  get PREDICTIVE_CREATE_COMPONENTS() {
    return PREDICTIVE_CREATE_COMPONENTS;
  }
  get CONFIG_MODE() {
    return CONFIG_MODE;
  }
}
